import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCostsByBlock, getCostLedger, CostByBlockItem, LedgerItem } from '../lib/api';
import { TrendingUp, Database } from 'lucide-react';

export default function Costs() {
  const [activeTab, setActiveTab] = useState<'summary' | 'ledger'>('summary');
  const [ledgerProvider, setLedgerProvider] = useState<string>('');
  const [ledgerBlock, setLedgerBlock] = useState<string>('');

  const { data: blockCosts } = useQuery({
    queryKey: ['costs-by-block'],
    queryFn: getCostsByBlock,
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['cost-ledger', ledgerProvider, ledgerBlock],
    queryFn: () => getCostLedger(ledgerProvider || undefined, ledgerBlock || undefined, 1, 100),
    enabled: activeTab === 'ledger',
  });

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 }).format(val);

  const providers = ['serpapi', 'pagespeed', 'crux', 'perplexity', 'crawl'];
  const blocks = [
    'keyword_search', 'keyword_generation', 'grid_search', 'perspective_search',
    'seo_audit', 'technical_health', 'content_quality', 'advanced_aeo_signals'
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Costs & Ledger</h1>
        <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'summary' 
                ? 'bg-emerald-500/10 text-emerald-400' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('ledger')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'ledger' 
                ? 'bg-emerald-500/10 text-emerald-400' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Call Ledger
          </button>
        </div>
      </div>

      {activeTab === 'summary' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-100">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Spend by Block
              </h2>
              <div className="space-y-3">
                {blockCosts?.items
                  .sort((a: CostByBlockItem, b: CostByBlockItem) => b.total_cost_usd - a.total_cost_usd)
                  .map((item: CostByBlockItem) => (
                    <div key={item.block} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div>
                        <p className="font-mono text-sm text-slate-200">{item.block}</p>
                        <p className="text-xs text-slate-500">{item.total_calls.toLocaleString()} calls</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-400">{formatCurrency(item.total_cost_usd)}</p>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(item.avg_cost_per_call)} / call
                        </p>
                      </div>
                    </div>
                  ))}
                {(!blockCosts?.items || blockCosts.items.length === 0) && (
                  <p className="text-slate-500 text-sm">No cost data available.</p>
                )}
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-100">
                <Database className="h-5 w-5 text-blue-400" />
                Provider Breakdown
              </h2>
              <div className="space-y-4">
                <p className="text-slate-400 text-sm">
                  <Database className="h-4 w-4 inline mr-1" />
                  For detailed provider breakdown, view the Call Ledger below or check the Dashboard for daily totals.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <select
              className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={ledgerProvider}
              onChange={(e) => setLedgerProvider(e.target.value)}
            >
              <option value="">All Providers</option>
              {providers.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
            </select>
            <select
              className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={ledgerBlock}
              onChange={(e) => setLedgerBlock(e.target.value)}
            >
              <option value="">All Blocks</option>
              {blocks.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">Operation</th>
                    <th className="px-4 py-3">Block</th>
                    <th className="px-4 py-3 text-right">Cost (USD)</th>
                    <th className="px-4 py-3 text-center">Cache Hit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {ledgerLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading ledger...</td>
                    </tr>
                  ) : ledgerData?.items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No ledger entries found.</td>
                    </tr>
                  ) : (
                    ledgerData?.items.map((item: LedgerItem) => (
                      <tr key={item.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                          {new Date(item.occurred_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 capitalize text-slate-300">{item.provider}</td>
                        <td className="px-4 py-3 font-mono text-slate-400">{item.operation}</td>
                        <td className="px-4 py-3 text-slate-300">{item.block || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-400">
                          {formatCurrency(item.unit_cost_usd)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.cache_hit ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-400">
                              No
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
